import { StrictMode, Component, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { reportError } from './lib/errorReporter'

// ── Self-capture: Bugpunch dogfoods its own error reporting ──

window.addEventListener('error', (e) => {
  reportError({
    message: e.message || 'Uncaught error',
    stack: e.error?.stack,
    url: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : location.href,
  });
});

window.addEventListener('unhandledrejection', (e) => {
  const err = e.reason instanceof Error ? e.reason : new Error(String(e.reason));
  reportError({ message: err.message, stack: err.stack, url: location.href });
});

// Vite ships per-bundle hashed chunks (e.g. AdminPage-DQj70hAm.js). When a
// new deploy lands, the index.html served to a fresh tab references new
// hashes while open tabs still hold the old hashes in memory. The next
// route change tries to lazy-import a chunk that no longer exists on the
// server and React throws "Failed to fetch dynamically imported module".
// Auto-reload on that specific error so users land on the new bundle.
const RELOAD_KEY = 'bp-chunk-reload-at';
function handleChunkLoadError(message: string) {
  if (!/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(message)) {
    return false;
  }
  // Guard against reload loops (e.g. CDN actually serving 404). Only reload
  // once per minute per session.
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
  if (Date.now() - last < 60_000) return false;
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  location.reload();
  return true;
}

window.addEventListener('vite:preloadError', (e: Event) => {
  // Vite's official escape hatch — fired right when a preload fails.
  e.preventDefault();
  if (!handleChunkLoadError('Failed to fetch dynamically imported module')) {
    // Fall through to normal error path if reload-throttled.
    reportError({ message: 'vite:preloadError (reload throttled)', url: location.href });
  }
});

window.addEventListener('error', (e) => {
  if (handleChunkLoadError(e.message || '')) e.preventDefault();
});
window.addEventListener('unhandledrejection', (e) => {
  const msg = e.reason instanceof Error ? e.reason.message : String(e.reason);
  if (handleChunkLoadError(msg)) e.preventDefault();
});

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    reportError({
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      url: location.href,
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100vh', background: '#0a0a0a', color: '#e4e4e7',
          fontFamily: 'system-ui', flexDirection: 'column', gap: '12px',
        }}>
          <h1 style={{ fontSize: '18px', fontWeight: 600 }}>Something went wrong</h1>
          <p style={{ color: '#71717a', fontSize: '14px', maxWidth: '400px', textAlign: 'center' }}>
            {this.state.error.message}
          </p>
          <button
            onClick={() => location.reload()}
            style={{
              marginTop: '8px', padding: '8px 20px', background: '#3b82f6',
              color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '14px', fontWeight: 500,
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
